import cn from 'classnames'
import React, { useCallback } from 'react'

import type { AdapterKitControlProps } from 'components/formKit/types'
import Icon from 'components/uiKit/Icon'
import type { IKitControlProps } from 'components/uiKit/KitTypes'
import { KitSize } from 'components/uiKit/KitTypes'
import { OptionLabel } from 'components/uiKit/KitUtils'
import type { IMenuOption } from 'components/uiKit/Menu'

import * as s from './FieldCardSelect.module.scss'

export type ICardOption<V extends string> = IMenuOption<V> & {
  hint?: string
}

export interface ICardSelectProps<V extends string> extends IKitControlProps<V> {
  options: ICardOption<V>[]
  cardHeight?: number
}

const FieldCardSelect = <V extends string>({
  name,
  onChange,
  options,
  value,
  cardHeight,
}: AdapterKitControlProps<ICardSelectProps<V>, V>) => {
  const handleSelect = useCallback(
    (selectedValue: V) => {
      onChange({ name, value: selectedValue })
    },
    [name, onChange],
  )

  return (
    <div className={s.root}>
      {options.map((option) => (
        <button
          className={cn(s.card, { [s.selected]: value === option.value })}
          key={option.value}
          onClick={() => handleSelect(option.value)}
          style={cardHeight ? { minHeight: cardHeight } : undefined}
          type='button'
        >
          {option.icon && <Icon name={option.icon} size={KitSize.M} />}
          <div className={s.textContainer}>
            <span className={s.label}>
              <OptionLabel option={option} />
            </span>
            {option.hint && <span className={s.hint}>{option.hint}</span>}
          </div>
        </button>
      ))}
    </div>
  )
}

export default React.memo(FieldCardSelect) as typeof FieldCardSelect
